import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";

// Focused single-column layout for the linear candidate INVITE journey
// (landing -> verify -> setup -> prep -> session -> scorecard). Centered
// ".page" chrome, distinct from HomeShell/RecruiterShell's authenticated
// header chrome.
//
// Two-state white-label lockup:
//   State 1 — EIC only: BrandMark + "EXECUTIVE INTERVIEW COACH" (no agency branding)
//   State 2 — Agency + EIC: agency logo / "POWERED BY" / BrandMark + EIC wordmark
// dark=true renders for the navy live-session screen (light text, --accent mark).

export function BrandLockup({
  agencyName = "Executive Interview Coach",
  logoUrl,
  logoFilter,
  dark = false,
  inline = false,
  align = "center",
}: {
  agencyName?: string;
  logoUrl?: string;
  logoFilter?: string;
  dark?: boolean;
  /** Single left-aligned row for horizontal chrome (e.g. HomeShell's header),
   *  instead of the centred vertical stack used on the narrow invite pages. */
  inline?: boolean;
  /** Non-inline variant only: "center" (default) for standalone page headers
   *  (every auth/public page). "left" for stacked-in-a-header contexts like
   *  RecruiterShell's mobile logo, where centering reads oddly next to the
   *  bell/menu icons on the same row. */
  align?: "center" | "left";
}) {
  // Deliberately NOT var(--accent). This is the Executive Interview Coach
  // waveform mark inside "Powered by …" — it carries the platform's identity,
  // not the agency's, so it holds house plum on every white-labelled portal.
  // The agency's own colour drives everything around it. (design-system/system.md)
  const markColor = "#7A6BE0"; // eslint-disable-line -- intentional house-plum literal
  const wordmarkColor = dark ? "rgba(255,255,255,.85)" : "var(--ink)";
  const poweredByColor = dark ? "rgba(255,255,255,.5)" : "var(--slate-500,#7C7590)";

  const eicRow = (
    <div className="flex items-center gap-1.5">
      <BrandMark size={15} color={markColor} />
      <span
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap"
        style={{ color: wordmarkColor }}
      >
        Executive Interview Coach
      </span>
    </div>
  );

  const isAgency = agencyName && agencyName !== "Executive Interview Coach";

  if (inline) {
    if (!isAgency) {
      return (
        <div className="flex items-center gap-2">
          <BrandMark size={20} color={markColor} />
          <span className="text-[15px] font-bold uppercase whitespace-nowrap" style={{ color: wordmarkColor }}>
            Executive Interview Coach
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-2.5">
        {logoUrl ? (
          /* The backdrop lives on this span, never on the <img> carrying the
             filter: a CSS filter paints its own element's background too, so
             sharing them crushes the whole plate to one colour. Defaults to
             transparent, so an agency with no brand colour is unchanged. */
          <span
            className="inline-flex items-center justify-center rounded-md px-2 py-1"
            style={{ background: "var(--logo-backdrop, transparent)" }}
          >
            <img
              src={logoUrl}
              alt={agencyName}
              className="h-6 max-w-[140px] object-contain"
              style={logoFilter ? { filter: logoFilter } : undefined}
            />
          </span>
        ) : (
          <span className="text-[15px] font-bold whitespace-nowrap" style={{ color: wordmarkColor }}>
            {agencyName}
          </span>
        )}
        {/* Below md, this wraps to its own line instead of hiding outright —
            a single row overflows off-screen for longer agency names, and
            hiding it entirely lost the platform attribution on mobile with
            no fallback (flagged separately, task_956e87e3). */}
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span aria-hidden="true" className="hidden md:inline" style={{ color: "var(--hair,#EFEBF7)" }}>|</span>
          <span
            className="font-mono text-[8px] md:text-[9px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap"
            style={{ color: poweredByColor }}
          >
            Powered by
          </span>
          <BrandMark size={12} color={markColor} />
          <span
            className="font-mono text-[8px] md:text-[9px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: poweredByColor }}
          >
            Executive Interview Coach
          </span>
        </div>
      </div>
    );
  }

  if (!isAgency) {
    return <div className={`flex flex-col ${align === "left" ? "items-start" : "items-center"}`}>{eicRow}</div>;
  }

  return (
    <div className={`flex flex-col gap-1.5 ${align === "left" ? "items-start text-left" : "items-center text-center"}`}>
      {logoUrl ? (
        /* Same rule as the inline variant above: backdrop on the wrapper,
           filter on the image, never both on one element. */
        <span
          className="inline-flex items-center justify-center rounded-md px-2.5 py-1.5"
          style={{ background: "var(--logo-backdrop, transparent)" }}
        >
          <img
            src={logoUrl}
            alt={agencyName}
            className="h-7 max-w-[160px] object-contain"
            style={logoFilter ? { filter: logoFilter } : undefined}
          />
        </span>
      ) : (
        <span
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: wordmarkColor }}
        >
          {agencyName}
        </span>
      )}
      <span
        className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: poweredByColor }}
      >
        Powered by
      </span>
      {eicRow}
    </div>
  );
}

export function CandidateFlow({
  agencyName,
  logoUrl,
  logoFilter,
  children,
  width = "narrow",
}: {
  agencyName?: string;
  logoUrl?: string;
  logoFilter?: string;
  children: ReactNode;
  width?: "narrow" | "wide";
}) {
  const { token } = useParams<{ token: string }>();
  const homeUrl = token ? `/invite/${token}/home` : undefined;

  const lockup = (
    <BrandLockup agencyName={agencyName} logoUrl={logoUrl} logoFilter={logoFilter} />
  );

  return (
    <div className="min-h-full bg-paper px-5 py-10 sm:py-14">
      <div className={`mx-auto ${width === "wide" ? "max-w-xl" : "max-w-md"}`}>
        <div className="mb-8 flex justify-center">
          {homeUrl ? (
            <a href={homeUrl} style={{ textDecoration: "none" }} aria-label="Go to home">
              {lockup}
            </a>
          ) : (
            lockup
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
